import React, { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { type UseFieldArrayUpdate, useFormContext } from 'react-hook-form';

import Trash from '@quick/assets/icons/trash.svg';
import { useUserDetails } from '@quick/auth';
import type {
  IModality,
  IServiceType,
} from '@quick/go-api-types/services/country/lines/[id]';

import { useAppTranslation } from '@/app/i18n/client';
import { Assistants } from '@/components/address';
import { useFormDataContext } from '@/components/demandForm';
import { useConfirmAddress } from '@/layers/core/deal/services/useConfirmAddress';
import { useDebounce } from '@/layers/core/deal/services/useDebounce';

import type { IForm } from '../types';

import Save from './Save';
import { Search } from './Search';
import { Task } from './Task';

export const Address = ({
  modality,
  serviceType,
  address,
  index,
  uuid,
  removeAddress,
  updateAddress,
}: {
  modality: IModality | undefined;
  modal?: boolean;
  serviceType?: IServiceType;
  address: IForm['stops'][number];
  index: number;
  uuid: string;
  removeAddress?: () => void;
  updateAddress: UseFieldArrayUpdate<IForm, 'stops'>;
}) => {
  const {
    formState: { errors },
  } = useFormContext<IForm>();

  const taskError =
    errors.stops?.[index]?.description?.message ||
    errors.stops?.[index]?.type_stop_id?.message;

  const addressError = errors.stops?.[index]?.address?.message;

  const cityError = errors.stops?.[index]?.city_id?.message;

  const error = addressError || cityError || taskError;

  const isAddressError = !!addressError;
  const isTaskError = !!taskError && !addressError && !cityError;

  const showDelete = !modality?.single_address && !!removeAddress;
  const showSave = !!useUserDetails().sub;
  const showGroup = showDelete || showSave;
  const { user, line } = useFormDataContext();
  const countries = line?.related_countries ?? [];

  const useFirstAddress = serviceType?.use_first_address;

  return (
    <div className="group relative flex flex-col items-center self-stretch">
      <div
        className={clsx(
          'grid grid-cols-[auto,1fr] items-center gap-4 self-stretch',
          showGroup && 'sm:grid-cols-[auto,1fr,auto]'
        )}
      >
        <span
          className="group-focus-within:bg-brand flex h-9 w-9 shrink-0 select-none items-center
            justify-center rounded-full border border-gray-100 text-xl font-semibold
            text-gray-400 group-focus-within:border-0 group-focus-within:text-gray-800
            dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {index + 1}
        </span>
        <div
          className={clsx(
            'flex min-w-0 flex-col gap-x-4 gap-y-2 md:flex-row',
            showGroup && 'row-span-2 sm:row-span-1'
          )}
        >
          {(!useFirstAddress || (useFirstAddress && index === 0)) && (
            <Search
              modal
              user_id={Number(user?.id) || undefined}
              user_country_id={user?.country_id}
              index={index}
              updateAddress={updateAddress}
              countries={countries}
            />
          )}
          <Task modal index={index} />
          {serviceType?.req_assistant && (
            <Assistants
              assistants={address.assistants}
              onAssistantChange={(val) => {
                updateAddress(index, {
                  ...address,
                  uuid,
                  assistants: parseInt(val) ?? 0,
                });
              }}
            />
          )}
        </div>
        {showGroup && (
          <div className="flex flex-col gap-4 md:flex-row md:gap-1">
            {showSave && <Save index={index} />}
            {showDelete && (
              <button
                className="group/rm aspect-square text-red-600"
                onClick={removeAddress}
                type="button"
              >
                <Trash className="size-8 fill-transparent group-hover/rm:fill-red-300 group-focus/rm:fill-red-300" />
              </button>
            )}
          </div>
        )}
      </div>
      {error && (
        <p
          className={clsx(
            'ml-16 text-xs font-semibold text-red-500 empty:hidden dark:text-red-300',
            isTaskError && 'md:ml-0 md:w-4/5 md:text-right',
            isAddressError && 'md:ml-0 md:w-4/6 md:text-left'
          )}
        >
          {error}
        </p>
      )}
      <AddressMessage index={index} />
    </div>
  );
};

function AddressMessage({ index }: { index: number }) {
  const { t } = useAppTranslation(['demand']);
  const { watch, setValue } = useFormContext<IForm>();
  const [address, city_id] = watch([
    `stops.${index}.address`,
    `stops.${index}.city_id`,
  ]);
  const debouncedAddress = useDebounce(address.trim(), 500);

  const prevCityIdRef = useRef<string | number | null>(null);
  const [cityChanged, setCityChanged] = useState(false);
  useEffect(() => {
    const prev = prevCityIdRef.current;
    const changed = prev !== null && String(prev) !== String(city_id);
    setCityChanged(changed);
    prevCityIdRef.current = city_id;
  }, [city_id]);

  const { error: errorConfirmingAddress } = useConfirmAddress({
    index,
    debouncedAddress,
    shouldConfirm:
      !!Number(city_id) && (debouncedAddress.length > 0 || cityChanged),
    city_id,
    setValue,
  });
  if (errorConfirmingAddress)
    return (
      <p
        className="ml-16 text-center text-xs font-semibold text-red-600 empty:hidden
          dark:text-red-300"
      >
        {t('demand:booking.errorConfirmingAddress')}
      </p>
    );
  return <CoordinatesAssigned index={index} />;
}

function CoordinatesAssigned({ index }: { index: number }) {
  const { watch } = useFormContext<IForm>();
  const { t } = useAppTranslation(['demand']);
  const [lat, lng] = watch([
    `stops.${index}.latitude`,
    `stops.${index}.longitude`,
  ]);
  if (lat || lng)
    return (
      <p className="dark:text-brand ml-16 text-xs font-semibold text-gray-800 empty:hidden">
        {t('demand:addresses.addressForm.coordinatesAssigned')}
      </p>
    );
  return null;
}
